"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { Ban, Gavel, Info, Tag, XCircle } from "lucide-react";
import { toast } from "sonner";
import { DurableResult } from "@/features/home-v2/money/DurableResult";
import { formatTokenPrice } from "@/lib/helpers";
import type { NftFinance } from "./useNftFinance";
const tr = (isFa: boolean) => (fa: string, en: string) => isFa ? fa : en;

/** The one primary action, drawn — the hook decides which (or none). */
function Primary(t0) {
  const $ = _c(8);
  const {
    f,
    block: t1
  } = t0;
  const block = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== block || $[1] !== f.isFa || $[2] !== f.openPrimary || $[3] !== f.primary) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const t = tr(f.isFa);
      const cls = `v2-btn ${block ? "v2-btn--lg v2-btn--block" : ""} v2-focus`;
      if (f.primary === "cancel-listing") {
        let t3;
        if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <XCircle aria-hidden={true} />;
          $[5] = t3;
        } else {
          t3 = $[5];
        }
        t2 = <button type="button" className={`${cls} v2-btn--ghost as-danger-ghost`} onClick={f.openPrimary}>{t3}{t("\u0644\u063A\u0648 \u0641\u0631\u0648\u0634", "Cancel listing")}</button>;
        break bb0;
      }
      if (f.primary === "list") {
        let t3;
        if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <Tag aria-hidden={true} />;
          $[6] = t3;
        } else {
          t3 = $[6];
        }
        t2 = <button type="button" className={cls} onClick={f.openPrimary}>{t3}{t("\u0644\u06CC\u0633\u062A \u0628\u0631\u0627\u06CC \u0641\u0631\u0648\u0634", "List for sale")}</button>;
        break bb0;
      }
      if (f.primary === "offer") {
        let t3;
        if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <Gavel aria-hidden={true} />;
          $[7] = t3;
        } else {
          t3 = $[7];
        }
        t2 = <button type="button" className={cls} onClick={f.openPrimary}>{t3}{t("\u062B\u0628\u062A \u0622\u0641\u0631", "Place an offer")}</button>;
        break bb0;
      }
    }
    $[0] = block;
    $[1] = f.isFa;
    $[2] = f.openPrimary;
    $[3] = f.primary;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  return null;
}
function Price(t0) {
  const $ = _c(11);
  const {
    f,
    size: t1
  } = t0;
  const size = t1 === undefined ? "big" : t1;
  const locale = f.isFa ? "fa-IR" : "en-US";
  const t2 = `ns-price ns-price--${size}`;
  const t3 = f.listingPrice as number;
  let t4;
  if ($[0] !== locale || $[1] !== t3) {
    t4 = formatTokenPrice(t3, locale);
    $[0] = locale;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t4) {
    t5 = <bdi>{t4}</bdi>;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = f.listingCurrencySymbol ?? "\u2014";
  let t7;
  if ($[5] !== t6) {
    t7 = <small>{t6}</small>;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t2 || $[8] !== t5 || $[9] !== t7) {
    t8 = <p className={t2}>{t5}{t7}</p>;
    $[7] = t2;
    $[8] = t5;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  return t8;
}

/**
 * THE COUNTER — the price (or that it is not for sale), the one action, and
 * what the viewer should know: an inactive NFT takes no offers (said, never
 * a dead button), and a listed NFT has no direct purchase — the way to buy
 * is an offer the owner accepts. Below 768px, while the counter is off
 * screen, the price and the action ride a dock above the tab bar.
 */
export function NftDesk(t0) {
  const $ = _c(2);
  const {
    f,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t = tr(f.isFa);
  const listed = Boolean(f.isListed && f.listingPrice);
  const ref = useRef(null);
  const [away, setAway] = useState(false);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const el = ref.current;
      if (!el || typeof IntersectionObserver === "undefined") {
        return;
      }
      const io = new IntersectionObserver(t4 => {
        const [e] = t4;
        return setAway(!e.isIntersecting && e.boundingClientRect.top < 0);
      });
      io.observe(el);
      return () => io.disconnect();
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  return <><section ref={ref} className={`ns-desk ${className}`} aria-label={t("\u062E\u0631\u06CC\u062F \u0648 \u0641\u0631\u0648\u0634", "Buy and sell")}><div className="ns-desk-top"><span className="ap-k">{listed ? t("\u0642\u06CC\u0645\u062A \u0641\u0631\u0648\u0634", "Listed price") : t("\u0648\u0636\u0639\u06CC\u062A \u0641\u0631\u0648\u0634", "Sale")}</span>{listed ? <span className="ns-seal" data-tone="up"><Tag aria-hidden={true} />{t("\u062F\u0631 \u0641\u0631\u0648\u0634", "Listed")}</span> : null}</div>{listed ? <Price f={f} /> : <p className="ns-unlisted">{t("\u0627\u06CC\u0646 NFT \u0627\u0644\u0627\u0646 \u0628\u0631\u0627\u06CC \u0641\u0631\u0648\u0634 \u06AF\u0630\u0627\u0634\u062A\u0647 \u0646\u0634\u062F\u0647 \u0627\u0633\u062A.", "This NFT is not listed for sale right now.")}</p>}{f.primary ? <Primary f={f} /> : <p className="ap-closed ns-inactive" role="status"><Ban aria-hidden={true} /><span><b>{t("\u0627\u06CC\u0646 NFT \u0641\u0639\u0627\u0644 \u0646\u06CC\u0633\u062A", "This NFT is not active")}</b>{t("\u0628\u0646\u0627\u0628\u0631\u0627\u06CC\u0646 \u0622\u0641\u0631\u06CC \u0631\u0648\u06CC \u0622\u0646 \u062B\u0628\u062A \u0646\u0645\u06CC\u200C\u0634\u0648\u062F.", "so it can't receive offers.")}</span></p>}{f.isListed && !f.isOwner && f.isNftActive ? <p className="ns-note"><Info aria-hidden={true} />{t("\u062E\u0631\u06CC\u062F \u0645\u0633\u062A\u0642\u06CC\u0645 \u0627\u06CC\u0646\u062C\u0627 \u0645\u0645\u06A9\u0646 \u0646\u06CC\u0633\u062A\u061B \u0622\u0641\u0631 \u0628\u062F\u0647\u06CC\u062F \u062A\u0627 \u0645\u0627\u0644\u06A9 \u0622\u0646 \u0631\u0627 \u0628\u067E\u0630\u06CC\u0631\u062F.", "This can't be bought directly here \u2014 place an offer for the owner to accept.")}</p> : null}</section>{f.primary ? <div className="ns-dock" data-away={away || undefined} inert={!away}>{listed ? <span className="ns-dock-price"><small>{t("\u0642\u06CC\u0645\u062A \u0641\u0631\u0648\u0634", "Listed price")}</small><Price f={f} size="small" /></span> : null}<Primary f={f} block={false} /></div> : null}</>;
}

/** What this browser did here — kept after a refresh, each dismissable (the legacy ledger). */
export function NftReceipts(t0) {
  const $ = _c(9);
  const {
    f
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== f) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const t = tr(f.isFa);
      if (f.records.length === 0) {
        t4 = null;
        break bb0;
      }
      t1 = "ns-receipts";
      t2 = t("\u0646\u062A\u06CC\u062C\u0647\u200C\u06CC \u06A9\u0627\u0631\u0647\u0627\u06CC \u0634\u0645\u0627 \u0631\u0648\u06CC \u0627\u06CC\u0646 \u0627\u062B\u0631", "Transaction results for this item");
      t3 = f.records.map(entry => <DurableResult lang={f.isFa ? "fa" : "en"} key={entry.requestId} outcome={entry.outcome === "success" ? "success" : "failure"} title={entry.title} description={entry.description} refs={entry.refs} labels={{
        copy: t("\u06A9\u067E\u06CC", "Copy"),
        open: t("\u0628\u0627\u0632 \u06A9\u0631\u062F\u0646", "Open")
      }} onCopy={value => {
        navigator.clipboard?.writeText(value);
        toast.success(t("\u06A9\u067E\u06CC \u0634\u062F", "Copied"));
      }} action={<button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={() => f.dismiss(entry.requestId)}>{t("\u0628\u0633\u062A\u0646 \u0627\u06CC\u0646 \u0646\u062A\u06CC\u062C\u0647", "Dismiss this result")}</button>} />);
    }
    $[0] = f;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t5 = <section className={t1} aria-label={t2}>{t3}</section>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}